import React, {useEffect, useState} from "react"
import { Button, Drawer, Accordion } from 'components/common';
import { useForm, Controller } from "react-hook-form";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { MdOutlineClose, MdOutlineReceiptLong, MdOutlineSave } from "react-icons/md";
import DatePicker from "react-datepicker";
import { BsBuildings } from "react-icons/bs";
import { MdOutlinePayments } from "react-icons/md";
import PropTypes from 'prop-types';
import { FaNotesMedical } from "react-icons/fa6";
import { LuFileOutput, LuCalendarDays, LuAtom, LuListChecks, LuTruck } from "react-icons/lu";
import { FaDrawPolygon } from "react-icons/fa";
import { adjustToNoon } from "helpers/utils";
import { updateSchedule } from "redux/crm/project/scheduleSlice";

import { INPUT_CLS as INPUT_CLASSES } from 'helpers/formStyles';
const ProjectScheduleForm = ({open, setOpen, record}) => {
  const {control, register, handleSubmit, setValue, reset} = useForm();
  const [activeKey, setActiveKey] = useState(["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"]);

  const dispatch = useDispatch();

  useEffect(() => {
    if(record) {
      Object.entries(record).forEach(([key, value]) => {
        switch(key)
        {
          case "apd":
            if (value && typeof value === "object") {
              Object.entries(value).forEach(([subKey, subValue]) => {
                if (subKey === "submission_date") {
                  setValue(`apd.submission_date`, subValue ? new Date(subValue) : null);
                }
                else {
                  setValue(`apd.${subKey}`, subValue);
                }
              });
            }
            break;
          case "rad":
            if (value && typeof value === "object") {
              Object.entries(value).forEach(([subKey, subValue]) => {
                if (subKey === "plan_date") {
                  setValue(`rad.plan_date`, subValue ? new Date(subValue) : null);
                }
                else {
                  setValue(`rad.${subKey}`, subValue);
                }
              });
            }
            break;
          case "fabrication_at_factory":
            if (value && typeof value === "object") {
              Object.entries(value).forEach(([subKey, subValue]) => {
                if (subKey === "plan_date") {
                  setValue(`fabrication_at_factory.plan_date`, subValue ? new Date(subValue) : null);
                }
                else if (subKey === "actual_date") {
                  setValue(`fabrication_at_factory.actual_date`, subValue ? new Date(subValue) : null);
                }
                else {
                  setValue(`fabrication_at_factory.${subKey}`, subValue);
                }
              });
            }
            break;
          case "delivery":
            if (value && typeof value === "object") {
              Object.entries(value).forEach(([subKey, subValue]) => {
                if (subKey === "plan_date") {
                  setValue(`delivery.plan_date`, subValue ? new Date(subValue) : null);
                }
                else if (subKey === "actual_date") {
                  setValue(`delivery.actual_date`, subValue ? new Date(subValue) : null);
                }
                else {
                  setValue(`delivery.${subKey}`, subValue);
                }
              });
            }
            break;
          case "erection_at_site":
            if (value && typeof value === "object") {
              Object.entries(value).forEach(([subKey, subValue]) => {
                if (subKey === "plan_date") {
                  setValue(`erection_at_site.plan_date`, subValue ? new Date(subValue) : null);
                }
                else if (subKey === "actual_date") {
                  setValue(`erection_at_site.actual_date`, subValue ? new Date(subValue) : null);
                }
                else {
                  setValue(`erection_at_site.${subKey}`, subValue);
                }
              });
            }
            break;
          case "punch_list":
            if (value && typeof value === "object") {
              Object.entries(value).forEach(([subKey, subValue]) => {
                if (subKey === "plan_date") {
                  setValue(`punch_list.plan_date`, subValue ? new Date(subValue) : null);
                }
                else if (subKey === "expected_date") {
                  setValue(`punch_list.expected_date`, subValue ? new Date(subValue) : null);
                }
                else if (subKey === "actual_date") {
                  setValue(`punch_list.actual_date`, subValue ? new Date(subValue) : null);
                }
                else {
                  setValue(`punch_list.${subKey}`, subValue);
                }
              });
            }
            break;
          case "hand_over":
            if (value && typeof value === "object") {
              Object.entries(value).forEach(([subKey, subValue]) => {
                if (subKey === "expected_date") {
                  setValue(`hand_over.expected_date`, subValue ? new Date(subValue) : null);
                }
                else if (subKey === "actual_date") {
                  setValue(`hand_over.actual_date`, subValue ? new Date(subValue) : null);
                }
                else {
                  setValue(`hand_over.${subKey}`, subValue);
                }
              });
            }
            break;
          default:
            setValue(key, value);
            break;
        }
      })
    }
    else {
      reset();
    }
  }, [setValue, reset, record, open]);

  const onSubmit = async (data) => {
    const DONE = "Done";
    const IN_PROGRESS = "In progress";

    data = {
      ...data,
      fabrication_at_factory: {
        ...data.fabrication_at_factory,
        duration: calDuration(data.fabrication_at_factory.plan_date, data.fabrication_at_factory.actual_date),
      },
      delivery: {
        ...data.delivery,
        duration: calDuration(data.delivery.plan_date, data.delivery.actual_date),
      },
      erection_at_site: {
        ...data.erection_at_site,
        duration: calDuration(data.erection_at_site.plan_date, data.erection_at_site.actual_date),
      },
      hand_over: {
        ...data.hand_over,
        status: data.hand_over.actual_date ? DONE : IN_PROGRESS,
      }
    }

    let response = null;

    try {
      response = await dispatch(updateSchedule({phaseId: record?.id, data })).unwrap();

      if (response && !response.error) {
        closeModal()
      }
    }
    catch(error) {
      console.error('Update schedule failed:', error)
      toast.error(error.message)
      return;
    }
  }

  const closeModal = () => {
    setOpen(false);
  }

  const handleAccordionSelect = (newActiveKey) => {
    setActiveKey(newActiveKey)
  }

  const calDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const duration = Math.ceil((end - start) / (1000 * 60 * 60 * 24)); // Convert milliseconds to days

    return duration >= 0 ? duration : 0; // Ensure non-negative duration
  }

  const fieldRow = (label, children) => (
    <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-mb-2 tw-items-center">
      <div className="tw-col-span-12 sm:tw-col-span-3 tw-text-sm tw-font-medium tw-text-slate-700 sm:tw-text-right">
        {label}
      </div>
      <div className="tw-col-span-12 sm:tw-col-span-7">
        {children}
      </div>
    </div>
  );

  const dateField = (name) => (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <DatePicker
          selected={field.value}
          onChange={(date) => {
            field.onChange(date)
            const adjustedDate = adjustToNoon(date);
            setValue(name, adjustedDate);
          }}
          className={INPUT_CLASSES}
          placeholderText="dd-MM-YYYY"
          dateFormat="dd-MM-yyyy"
          fixedHeight
        />
      )}
    />
  );

  const sectionHeader = (icon, label) => (
    <span className="tw-font-medium tw-text-slate-900">
      {icon} {label}
    </span>
  );

  return (
    <Drawer
      show={open}
      onHide={closeModal}
      placement="right"
      backdropClick={false}
      width="50%"
      className="tw-text-sm"
      title={<><LuCalendarDays /> Edit schedule</>}
      footer={(
        <>
          <Button size="sm" type="submit" variant="primary" className="tw-mr-3" form="project-schedule-form"><MdOutlineSave /> Update</Button>
          <Button size="sm" variant="danger" onClick={closeModal}><MdOutlineClose /> Close</Button>
        </>
      )}
    >
      <form id="project-schedule-form" onSubmit={handleSubmit(onSubmit)}>
        <Accordion
          className="tw-text-sm tw-mt-3"
          activeKeys={activeKey}
          onChange={handleAccordionSelect}
        >
          {/* Project information */}
          <Accordion.Item eventKey="0" header={sectionHeader(<BsBuildings />, '1. Project information')}>
            <div className="tw-p-4">
              {fieldRow('Job number', (
                <input
                  type="text"
                  className={INPUT_CLASSES}
                  {...register("building.job_number")}
                  disabled
                />
              ))}

              {fieldRow('Building', (
                <input
                  type="text"
                  className={INPUT_CLASSES}
                  {...register("building.name")}
                  disabled
                />
              ))}

              {fieldRow('Phase', (
                <input
                  type="text"
                  className={INPUT_CLASSES}
                  {...register("name")}
                  disabled
                />
              ))}

              {fieldRow('Description', (
                <input
                  type="text"
                  placeholder="Description"
                  className={`${INPUT_CLASSES} tw-capitalize`}
                  {...register("description")}
                  disabled
                />
              ))}
            </div>
          </Accordion.Item>

          {/* APD */}
          <Accordion.Item eventKey="1" header={sectionHeader(<FaDrawPolygon />, '2. APD')}>
            <div className="tw-p-4">
              {fieldRow('Submission date', dateField("apd.submission_date"))}
            </div>
          </Accordion.Item>

          {/* RAD */}
          <Accordion.Item eventKey="2" header={sectionHeader(<FaDrawPolygon />, '3. RAD')}>
            <div className="tw-p-4">
              {fieldRow('Plan date', dateField("rad.plan_date"))}
            </div>
          </Accordion.Item>

          {/* PROCUREMNET MATERIAL */}
          <Accordion.Item eventKey="3" header={sectionHeader(<LuAtom />, '4. Procurement material')}>
            <div className="tw-p-4">
              {fieldRow('Duration', (
                <input
                  type="text"
                  placeholder="Duration"
                  className={`${INPUT_CLASSES} tw-capitalize`}
                  {...register("procurement_material_duration")}
                />
              ))}
            </div>
          </Accordion.Item>

          {/* Fabrication PLAN at FACTORY */}
          <Accordion.Item eventKey="4" header={sectionHeader(<MdOutlinePayments />, '5. Fabrication plan at factory')}>
            <div className="tw-p-4">
              {fieldRow('Plan date', dateField("fabrication_at_factory.plan_date"))}
            </div>
          </Accordion.Item>

          {/* Delivery plan */}
          <Accordion.Item eventKey="5" header={sectionHeader(<LuTruck />, '6. Delivery plan')}>
            <div className="tw-p-4">
              {fieldRow('Plan date', dateField("delivery.plan_date"))}
            </div>
          </Accordion.Item>

          {/* ERECTION AT SITE (CMD) */}
          <Accordion.Item eventKey="6" header={sectionHeader(<MdOutlineReceiptLong />, '7. Erection at site (CMD)')}>
            <div className="tw-p-4">
              {fieldRow('Plan date', dateField("erection_at_site.plan_date"))}
              {fieldRow('Actual date', dateField("erection_at_site.actual_date"))}
            </div>
          </Accordion.Item>

          {/* Punch list */}
          <Accordion.Item eventKey="7" header={sectionHeader(<LuListChecks />, '8. Punch list')}>
            <div className="tw-p-4">
              {fieldRow('Plan date', dateField("punch_list.plan_date"))}
              {fieldRow('Expected date', dateField("punch_list.expected_date"))}
              {fieldRow('Actual date', dateField("punch_list.actual_date"))}
            </div>
          </Accordion.Item>

          {/* Hand Over */}
          <Accordion.Item eventKey="8" header={sectionHeader(<LuFileOutput />, '9. Hand over')}>
            <div className="tw-p-4">
              {fieldRow('Expected date', dateField("hand_over.expected_date"))}
              {fieldRow('Actual date', dateField("hand_over.actual_date"))}
            </div>
          </Accordion.Item>

          {/* Note */}
          <Accordion.Item eventKey="9" header={sectionHeader(<FaNotesMedical />, '10. Note')}>
            <div className="tw-p-4">
              {fieldRow('Remark', (
                <textarea
                  rows={6}
                  placeholder="Remark"
                  className={INPUT_CLASSES}
                  {...register("pmd_remark")}
                />
              ))}
            </div>
          </Accordion.Item>
        </Accordion>
      </form>
    </Drawer>
  )
};
ProjectScheduleForm.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  title: PropTypes.string,
  record: PropTypes.shape({
    project: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      job_number: PropTypes.string,
      delivery_term: PropTypes.string,
      destination_port: PropTypes.string,
    }),
    building: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    }),
    id: PropTypes.string,
    name: PropTypes.string
    // Add other properties of record as needed
  }),
};

export default ProjectScheduleForm;
